import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Tag, 
  Typography, 
  Row, 
  Col, 
  Space, 
  Button, 
  Modal, 
  Image, 
  Timeline,
  Rate,
  Empty,
  Skeleton,
  message
} from 'antd';
import { 
  HistoryOutlined, 
  EyeOutlined, 
  StarOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
// Customer portal service removed
import { useAuth } from '../../../contexts/AuthContext';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const ServiceHistory = () => {
  const [serviceHistory, setServiceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser) {
      fetchServiceHistory();
    }
  }, [currentUser]);

  const fetchServiceHistory = async () => {
    try {
      setLoading(true);
      // Customer portal service removed - using empty history
      setServiceHistory([]);
    } catch (error) {
      console.error('Error fetching service history:', error);
      message.error('Failed to load service history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (service) => {
    setSelectedService(service);
    setDetailModalVisible(true);
  };

  const getServiceIcon = (serviceType) => {
    const icons = {
      'lawn-care': '🌱',
      'painting': '🎨',
      'cleaning': '🧽',
      'maintenance': '🔧',
      'gutter-cleaning': '🏠',
      'landscaping': '🌿'
    };
    return icons[serviceType] || '🔧';
  };

  const columns = [
    {
      title: 'Service',
      dataIndex: 'serviceType',
      key: 'serviceType',
      render: (serviceType, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.2rem' }}>{getServiceIcon(serviceType)}</span>
            <Text strong>{record.serviceName}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            {record.notes && record.notes.length > 50 
              ? `${record.notes.substring(0, 50)}...` 
              : record.notes || 'No notes'
            }
          </Text>
        </div>
      )
    },
    {
      title: 'Date',
      dataIndex: 'scheduledDate',
      key: 'scheduledDate',
      render: (date) => (
        <div>
          <div>{dayjs(date).format('MMM DD, YYYY')}</div>
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            {dayjs(date).format('dddd')}
          </Text>
        </div>
      )
    },
    {
      title: 'Time',
      dataIndex: 'scheduledTime',
      key: 'scheduledTime',
      render: (time) => (
        <div>
          <ClockCircleOutlined style={{ marginRight: '0.25rem' }} />
          {time}
        </div>
      )
    },
    {
      title: 'Duration',
      dataIndex: 'estimatedDuration',
      key: 'estimatedDuration',
      render: (duration) => `${duration} min`
    },
    {
      title: 'Price',
      dataIndex: 'estimatedPrice',
      key: 'estimatedPrice',
      render: (price) => (
        <div>
          <DollarOutlined style={{ marginRight: '0.25rem' }} />
          <Text strong>${price}</Text>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          Completed
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button 
          type="text" 
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        >
          View Details
        </Button>
      )
    }
  ];

  if (loading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  if (serviceHistory.length === 0) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No service history yet"
        >
          <Text type="secondary">
            Your completed services will appear here
          </Text>
        </Empty>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              <HistoryOutlined style={{ marginRight: '0.5rem' }} />
              Service History
            </Title>
            <Text type="secondary">
              View your completed home maintenance services
            </Text>
          </div>
          <div>
            <Text strong>{serviceHistory.length}</Text> completed services
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={serviceHistory}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="middle"
        />
      </Card>

      {/* Service Detail Modal */}
      <Modal
        title="Service Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedService && (
          <div>
            <Row gutter={[16, 16]} style={{ marginBottom: '1.5rem' }}>
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '2rem' }}>{getServiceIcon(selectedService.serviceType)}</span>
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      {selectedService.serviceName}
                    </Title>
                    <Text type="secondary">
                      {dayjs(selectedService.scheduledDate).format('MMMM DD, YYYY')} at {selectedService.scheduledTime}
                    </Text>
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <ClockCircleOutlined style={{ fontSize: '1.5rem', color: '#1890ff', marginBottom: '0.5rem' }} />
                    <div><Text strong>Duration</Text></div>
                    <div>{selectedService.estimatedDuration} minutes</div>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <DollarOutlined style={{ fontSize: '1.5rem', color: '#52c41a', marginBottom: '0.5rem' }} />
                    <div><Text strong>Price</Text></div>
                    <div>${selectedService.estimatedPrice}</div>
                  </div>
                </Card>
              </Col>
            </Row>

            {selectedService.notes && (
              <div style={{ marginTop: '1.5rem' }}>
                <Title level={5}>Service Notes</Title>
                <Paragraph>{selectedService.notes}</Paragraph>
              </div>
            )}

            {/* Photos Section */}
            {(selectedService.beforePhotos?.length > 0 || selectedService.afterPhotos?.length > 0) && (
              <div style={{ marginTop: '1.5rem' }}>
                <Title level={5}>Service Photos</Title>
                
                {selectedService.beforePhotos?.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <Text strong>Before Photos:</Text>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {selectedService.beforePhotos.map((photo, index) => (
                        <Image
                          key={index}
                          width={100}
                          height={100}
                          src={photo.url}
                          alt={`Before ${index + 1}`}
                          style={{ objectFit: 'cover', borderRadius: '4px' }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {selectedService.afterPhotos?.length > 0 && (
                  <div>
                    <Text strong>After Photos:</Text>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {selectedService.afterPhotos.map((photo, index) => (
                        <Image
                          key={index}
                          width={100}
                          height={100}
                          src={photo.url}
                          alt={`After ${index + 1}`}
                          style={{ objectFit: 'cover', borderRadius: '4px' }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
              <Button 
                type="primary" 
                onClick={() => setDetailModalVisible(false)}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ServiceHistory;
